/**
 * Order widgets (#1802, extended to the eight cell projections in #1805 A4c).
 *
 * The augmentation below is how a feature opens the closed set of widget names
 * without core knowing anything about orders. It keys on the FINAL package
 * name (design §7.4), which is why it kept working untouched when core became
 * a real workspace package (`packages/resource-kit-core`, #2043).
 *
 * FOUR of the eight fork on the surface they render on, and `WidgetProps.surface`
 * is the carrier: `displayProps` in
 * `packages/resource-kit-ui/react/registry/field-type-registry.tsx::displayProps`
 * sets `surface: mode`, and the detail side's surface comes from the detail
 * columns built at the page root.
 * Absent ⇒ `'list'` for direct widget consumers, which is why every fork below
 * tests for `'detail'` rather than for `'list'`.
 *
 * All eight declare `csvText: rawCsv`. That is not decoration: a field with a
 * bespoke `render` exports `stringifyRaw(value)`
 * (`packages/resource-kit-ui/react/registry/export-text.ts::formatFieldTextForExport`), and
 * a widget that OMITS `csvText` has it backfilled from the field TYPE's
 * projection (`packages/resource-kit-core/widgets.ts::withTypeDefaultProjections`). Dropping it
 * would have silently changed what an operator downloads for all eight, which
 * `lib/__tests__/csv-export-lock.test.ts` holds frozen.
 *
 * This module is deliberately SERVICE-FREE at the top level. `features/widgets.ts`
 * is evaluated by `src/test-setup.ts` for every suite, ahead of any `vi.mock`,
 * so a service reached from here is instantiated against the real `@lib/api`
 * client and every suite that mocks HTTP starts hitting the network — that cost
 * 19 red tests in slice A1 (#1846). `OnfleetTasksBadge` is the one projection
 * here that needs a service, and it reaches for it through a DYNAMIC import
 * inside the hover handler, which is not on the barrel's static graph.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  CopyButton,
  EntityLink,
  ExternalServiceLink,
  stringifyRaw,
} from '@resource-kit/ui/react';
import { cn, enumBadgeVariant } from '@resource-kit/ui';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import { EXTERNAL_URLS } from '@/constants/config';
import { lookupEnumLabelOrWarn } from '@lib/enum-label';
import {
  formatAddress,
  formatAddressOrNull,
  isEmptyOrderAddress,
  pickDefaultCustomerAddress,
} from '@/lib/formatAddress';
import { formatCurrency, formatDateTimeCompact } from '@lib/utils';
import { DisplayTable, type DisplayColumn } from '@components/ui/DisplayTable';
import { AddressLink } from '@components/ui/AddressLink';
import { RawDataDisclosure } from '@components/ui/RawDataDisclosure';
import { lineItemProductPath, lineItemTitle, type LineItem } from '@lib/line-items';
import i18n from '@/i18n';
import type { FulfillmentJob } from '@/services';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    statusBadge: { types: 'enum'; modes: 'list' | 'form' };
    orderName: { types: 'text'; modes: 'list' | 'form' };
    orderCustomerEmail: { types: 'email'; modes: 'list' | 'form' };
    orderFulfillmentStatus: { types: 'enum'; modes: 'list' | 'form' };
    orderLineItems: { types: 'array'; modes: 'list' | 'form' };
    orderTaskCount: { types: 'number'; modes: 'list' | 'form' };
    orderShippingAddress: { types: 'json'; modes: 'list' | 'form' };
    orderProcessedAt: { types: 'datetime'; modes: 'list' | 'form' };
    orderDeliveryDate: { types: 'date'; modes: 'list' | 'form' };
  }
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The row members these projections read.
 *
 * Written as an open record of the members actually used, the way
 * `features/audit/widgets.tsx` does, rather than importing the wire type: a
 * widget is handed `record: unknown` and a row missing a member must degrade
 * the way the legacy renderer did, not throw inside a cell.
 */
interface OrderCellRecord {
  item_count?: number;
  id?: string;
  shopify_id?: string;
  order_number?: string;
  customer_id?: string | null;
  phone?: string | null;
  currency?: string | null;
  customer?: { addresses?: string | null } | null;
}

const recordOf = (record: unknown): OrderCellRecord =>
  typeof record === 'object' && record !== null ? record : {};

/**
 * A status projection is a COMPONENT, not a render callback (design §5.2
 * discipline 1): it calls `useTranslation`, which a callback could not.
 */
export function OrderStatusBadge({ value, field }: WidgetProps) {
  const { t } = useTranslation();
  const option = field.options?.find((candidate) => String(candidate.value) === String(value));

  if (option === undefined) return <span className="text-sm text-muted-foreground">-</span>;

  return (
    <Badge variant={enumBadgeVariant(option.color)}>
      {t(option.label, { defaultValue: option.label })}
    </Badge>
  );
}

export function OrderStatusSelect({
  value,
  field,
  onChange,
}: WidgetProps & Required<Pick<WidgetProps, 'onChange'>>) {
  const { t } = useTranslation();
  const fieldLabel = t(field.label, { defaultValue: field.label });

  return (
    <Select
      value={value === null || value === undefined ? '' : String(value)}
      onValueChange={onChange}
      // `items` feeds Select.Value the label; without it the trigger
      // shows the raw stored value until the popup has mounted once.
      items={Object.fromEntries(
        (field.options ?? []).map((option) => [
          String(option.value),
          t(option.label, { defaultValue: option.label }),
        ]),
      )}
    >
      <SelectTrigger>
        <SelectValue placeholder={t('resource.selectField', { field: fieldLabel })} />
      </SelectTrigger>
      <SelectContent>
        {(field.options ?? []).map((option) => (
          <SelectItem key={String(option.value)} value={String(option.value)}>
            {t(option.label, { defaultValue: option.label })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The order name plus its drill-in link. LIST ONLY appends the Shopify
 * external-link icon: the detail page already IS the order, so a second way to
 * leave it is noise there (#1017 dedups the `#{order_number}` subtitle, which
 * on a Shopify-named order is byte-identical to the name).
 */
function OrderName({ value, record, surface }: WidgetProps) {
  const row = recordOf(record);
  const orderName = value as string;
  const shopifyUrl = EXTERNAL_URLS.shopifyAdmin('orders', row.shopify_id as string);
  const orderNumberLabel = row.order_number != null ? `#${row.order_number}` : '';
  const showOrderNumber = orderNumberLabel !== '' && orderNumberLabel !== (value as string);
  return (
    <div className="flex items-center gap-1.5">
      <div>
        <span className="inline-flex items-center gap-1">
          <EntityLink to={`/orders/${row.id}`} className="font-mono text-sm font-medium">
            {orderName}
          </EntityLink>
          {orderName && <CopyButton value={orderName} />}
        </span>
        {showOrderNumber && <div className="text-xs text-muted-foreground">{orderNumberLabel}</div>}
      </div>
      {surface !== 'detail' && shopifyUrl && (
        <ExternalServiceLink href={shopifyUrl} ariaLabel={i18n.t('ui.openInShopify')} />
      )}
    </div>
  );
}

/**
 * Customer identity in the email column.
 *
 * DETAIL renders the plain address, because `OrderCustomerSection` owns name /
 * link / phone / metrics at the top of the page (#1185). The LIST keeps the
 * compact customer-link + phone treatment, which is the only place those facts
 * appear there.
 */
function OrderCustomerEmail({ value, record, surface }: WidgetProps) {
  const row = recordOf(record);
  const email = value as string;
  if (surface === 'detail') {
    return email ? (
      <span className="text-sm text-foreground" title={email}>
        {email}
      </span>
    ) : (
      <span className="text-muted-foreground text-sm">{i18n.t('order.noCustomer')}</span>
    );
  }
  return (
    <div className="max-w-[200px]">
      {email ? (
        <div>
          {row.customer_id ? (
            <div className="flex items-center gap-1">
              <EntityLink
                to={`/customers/${row.customer_id}`}
                className="truncate text-sm font-medium block"
              >
                {email}
              </EntityLink>
              <CopyButton value={email} />
            </div>
          ) : (
            <div className="truncate text-sm font-medium text-foreground" title={email}>
              {email}
            </div>
          )}
          {row.phone && <div className="text-xs text-muted-foreground">{row.phone}</div>}
        </div>
      ) : (
        <span className="text-muted-foreground text-sm">{i18n.t('order.noCustomer')}</span>
      )}
    </div>
  );
}

/**
 * Shopify omits `fulfillment_status` (null) until an order ships, which the
 * generic enum renderer showed as a bare "-" (#1270). Treat the absent state as
 * the real domain value `unfulfilled` and render its localized badge.
 *
 * Reads the closed set off `field.options` — the MODEL's table — rather than
 * importing it from `schema.tsx`, which would pull `orderAPI` onto this
 * module's static graph. See the file header.
 */
function OrderFulfillmentStatus({ value, field }: WidgetProps) {
  const status = value == null || value === '' ? 'unfulfilled' : String(value);
  const options = field.options ?? [];
  const option = options.find((candidate) => String(candidate.value) === status);
  // Route the label through the SOT helper so an unknown wire status renders
  // `[unknown:*]` and warns instead of leaking a bare string (the #345 /
  // EQD §3 silent-fallback prohibition).
  const label = lookupEnumLabelOrWarn(status, options, 'order fulfillment status');
  return (
    <Badge variant={enumBadgeVariant(option?.color)} data-testid={`enum-badge-${status}`}>
      {label}
    </Badge>
  );
}

/**
 * Return the `line_items` array as-is, or `[]` for a missing / malformed value.
 *
 * ONE wire shape reaches this helper (#1222 / #1409): a verbatim Shopify
 * line-item array (`OrderSummary['line_items']`). Both the `/api/orders` list
 * projection and the flattened detail read hand it an array — the detail seam
 * no longer re-serialises to a JSON string — so the earlier array-vs-string
 * branch is gone. `value` still arrives typed `unknown`, so a runtime guard
 * remains: a non-array is a malformed payload (broken Shopify sync) →
 * `console.warn` (to the browser console) + `[]` so the row still renders.
 *
 * It lives HERE rather than in `schema.tsx` because the projection that needs
 * it does: the widget module may not import the schema, which carries the
 * service. Exported so the unit test can import the production helper directly
 * (CLAUDE.md test rule §1).
 */
export const parseLineItems = (lineItems: unknown): unknown[] => {
  if (lineItems == null || lineItems === '') return [];
  if (Array.isArray(lineItems)) return lineItems;
  console.warn('[orders] line_items is not an array', lineItems);
  return [];
};

/**
 * The line-item element shape moved to `@lib/line-items` in #1969: the
 * delivery job's own table renders the same elements off the same stored
 * blob, and two copies of the shape are two chances to disagree about what a
 * product id looks like. The DOOR stayed here — see `parseLineItems` above for
 * why the two surfaces must not share one.
 */

/**
 * #1201 — the list keeps the compact aggregate count; DETAIL renders the
 * itemized table (title / quantity / unit price / subtotal) so operators can
 * see WHAT was ordered, not just how many rows. Zero-price lines render like
 * any other row.
 *
 * #1969 moved the markup onto the shared `DisplayTable` — the hand-rolled
 * `<table>` here was the only itemized table in the app and the delivery job
 * needed the same one — and added the two affordances that table lacked: the
 * product title links into this admin's own product record, and the verbatim
 * blob the table summarises is one disclosure away. Every money rule below is
 * unchanged, the em-dash of #1222 especially.
 */
function OrderLineItemTitle({ item }: { item: LineItem }) {
  const title = lineItemTitle(item);
  const href = lineItemProductPath(item);
  // A line naming no product — a tip (#1694), a legacy row — is plain text.
  // A link to `/products/null` would be worse than no link at all.
  if (title === null) return <span className="text-muted-foreground">—</span>;
  return href === null ? (
    <span className="text-foreground">{title}</span>
  ) : (
    <EntityLink to={href}>{title}</EntityLink>
  );
}

/**
 * The money projection of one line. A MISSING price (undefined/null) means the
 * stored snapshot never captured a unit price — rendering US$0.00 fabricates
 * data (#1222), so both money columns show an honest em-dash. A present but
 * non-numeric price still degrades to $0.00 (unchanged): the value exists, it
 * is just malformed.
 */
function moneyOf(item: LineItem, currency: string) {
  const quantity = item.quantity ?? 0;
  const hasPrice = item.price !== undefined && item.price !== null;
  const parsed = Number(item.price);
  const unitPrice = Number.isFinite(parsed) ? parsed : 0;
  return {
    quantity,
    hasPrice,
    unitPrice: hasPrice ? formatCurrency(unitPrice, currency) : '—',
    subtotal: hasPrice ? formatCurrency(unitPrice * quantity, currency) : '—',
  };
}

function OrderLineItems({ value, record, surface }: WidgetProps) {
  const row = recordOf(record);
  if (surface !== 'detail') {
    return (
      <span className="font-semibold text-center text-foreground">
        {typeof row.item_count === 'number' ? row.item_count : '—'}
      </span>
    );
  }

  const items = parseLineItems(value) as LineItem[];
  const currency = row.currency || 'USD';

  if (items.length === 0) {
    return <span className="text-sm text-muted-foreground">{i18n.t('lineItem.empty')}</span>;
  }

  const columns: DisplayColumn<LineItem>[] = [
    {
      key: 'title',
      header: i18n.t('lineItem.title'),
      cell: (item) => <OrderLineItemTitle item={item} />,
    },
    {
      key: 'quantity',
      header: i18n.t('lineItem.quantity'),
      align: 'right',
      cell: (item) => moneyOf(item, currency).quantity,
    },
    {
      key: 'price',
      header: i18n.t('lineItem.price'),
      align: 'right',
      cellClassName: 'text-muted-foreground',
      cell: (item) => moneyOf(item, currency).unitPrice,
    },
    {
      key: 'subtotal',
      header: i18n.t('lineItem.subtotal'),
      align: 'right',
      cell: (item) => {
        const money = moneyOf(item, currency);
        return (
          <span className={money.hasPrice ? 'text-foreground' : 'text-muted-foreground'}>
            {money.subtotal}
          </span>
        );
      },
    },
  ];

  return (
    <div>
      <DisplayTable columns={columns} rows={items} ariaLabel={i18n.t('lineItem.title')} />
      <RawDataDisclosure value={items} />
    </div>
  );
}

/** Status dot per fulfillment-job status; the label stays foreground (ADR-0043). */
const JOB_STATUS_DOT = {
  planned: 'bg-status-unassigned',
  dispatched: 'bg-status-assigned',
  in_progress: 'bg-status-in-progress',
  completed: 'bg-success',
  exception: 'bg-destructive',
  cancelled: 'bg-destructive',
} as const satisfies Record<FulfillmentJob['status'], string>;

function OrderTaskCount({ record }: WidgetProps) {
  const row = recordOf(record);
  const shopifyId = row.shopify_id as string;
  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<FulfillmentJob[] | null>(null);

  const fetchJobs = async () => {
    if (jobs || loading) return;
    setLoading(true);
    try {
      const { orderAPI } = await import('@/services');
      const res = await orderAPI.getOrderTasks(shopifyId);
      setJobs(res.items);
    } catch (err) {
      // Hover popover is non-critical — fall back to empty list so the
      // UI shows the localized "no tasks" state rather than spinning
      // forever. Surface the error to the browser console so a broken
      // endpoint is still visible to whoever has devtools open.
      console.warn('[orders] failed to fetch fulfillment jobs for order', shopifyId, err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        setHover(true);
        fetchJobs();
      }}
      onMouseLeave={() => setHover(false)}
    >
      <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-colors bg-secondary text-muted-foreground">
        —
      </span>
      {hover && (
        <div className="absolute z-20 mt-2 w-80 p-3 bg-popover border border-border shadow-lg rounded-md text-popover-foreground animate-in fade-in-0 zoom-in-95">
          <div className="text-sm font-medium mb-2">{i18n.t('order.recentTasks')}</div>
          {loading ? (
            <div className="text-muted-foreground text-sm">{i18n.t('ui.loading')}</div>
          ) : jobs && jobs.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-auto">
              {jobs.slice(0, 5).map((job) => (
                <div key={job.id} className="text-xs p-2 rounded border border-border bg-card">
                  <div className="flex justify-between items-center mb-1">
                    {/* Fulfillment-job status is a closed wire enum (planned / dispatched /
                        in_progress / completed / exception / cancelled), shown as the raw
                        wire value. The dot carries the status colour; the text stays
                        foreground (ADR-0043). */}
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase text-foreground">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          // getOrderTasks is not zod-parsed, so a status the backend
                          // adds later can reach here; fall back to the unassigned dot.
                          JOB_STATUS_DOT[job.status] ?? 'bg-status-unassigned',
                        )}
                      />
                      {job.status}
                    </span>
                    {job.shipping_date && (
                      <span className="text-muted-foreground">{job.shipping_date}</span>
                    )}
                  </div>
                  {job.onfleet_task_id && (
                    <div className="mt-1">
                      <ExternalServiceLink
                        href={EXTERNAL_URLS.onfleetTask(job.onfleet_task_id)}
                        label="Onfleet"
                        // "Onfleet" alone is a brand label, not an
                        // action — set the ariaLabel to the verbed
                        // string so screen readers convey intent.
                        ariaLabel={i18n.t('ui.openInOnfleet')}
                      />
                    </div>
                  )}
                </div>
              ))}
              {jobs.length > 5 && (
                <div className="text-xs text-muted-foreground">
                  {i18n.t('order.moreItems', { count: jobs.length - 5 })}
                </div>
              )}
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">{i18n.t('order.noTasks')}</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * #1148 — the shipping address with a customer-default fallback.
 *
 * Some orders (GAS-era habit) carry NO shipping address; it lives on the
 * customer's `default_address` instead, and the stored `shipping_address` is
 * then NULL (new writes) or the legacy all-empty-string skeleton. In DETAIL
 * mode ONLY, fall back to the embedded customer's default address and label the
 * source so the operator knows where it came from. List rows do not carry the
 * embedded customer and keep the plain render.
 */
function OrderShippingAddress({ value, record, surface }: WidgetProps) {
  const row = recordOf(record);
  const isEmpty = isEmptyOrderAddress(value);
  const shouldFallback = surface === 'detail' && isEmpty;

  // `isEmpty` is checked first: a country-only skeleton ("USA") is not an
  // address, so it must not become a map link (#2689).
  const text = isEmpty ? null : formatAddressOrNull(value);
  if (text !== null) {
    return <AddressLink address={text} />;
  }

  const fallbackAddress = shouldFallback
    ? pickDefaultCustomerAddress(row.customer?.addresses)
    : null;
  const fallbackText = formatAddressOrNull(fallbackAddress);

  if (fallbackText !== null) {
    // The source annotation stays OUTSIDE the link (#2689).
    return (
      <div className="text-sm">
        <AddressLink address={fallbackText} />
        <span className="ml-1.5 text-xs text-muted-foreground">
          ({i18n.t('order.customerDefaultAddress')})
        </span>
      </div>
    );
  }

  return <span className="text-sm text-muted-foreground">{formatAddress(value)}</span>;
}

function OrderProcessedAt({ value }: WidgetProps) {
  const v = value as string | null;
  return v ? (
    <span className="text-sm text-muted-foreground">{formatDateTimeCompact(v)}</span>
  ) : (
    <span className="text-muted-foreground">-</span>
  );
}

function OrderDeliveryDate({ value }: WidgetProps) {
  const v = value as string | null;
  return v ? (
    <span className="text-sm text-foreground">{v}</span>
  ) : (
    <span className="text-muted-foreground">-</span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerOrderWidgets(): void {
  registerWidget('statusBadge', {
    display: OrderStatusBadge,
    input: OrderStatusSelect,
    supportedTypes: ['enum'],
    modes: ['list', 'form'],
  });
  registerWidget('orderName', {
    display: OrderName,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list', 'form'],
  });
  registerWidget('orderCustomerEmail', {
    display: OrderCustomerEmail,
    csvText: rawCsv,
    supportedTypes: ['email'],
    modes: ['list', 'form'],
  });
  registerWidget('orderFulfillmentStatus', {
    display: OrderFulfillmentStatus,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list', 'form'],
  });
  registerWidget('orderLineItems', {
    display: OrderLineItems,
    csvText: rawCsv,
    supportedTypes: ['array'],
    modes: ['list', 'form'],
  });
  registerWidget('orderTaskCount', {
    display: OrderTaskCount,
    csvText: rawCsv,
    supportedTypes: ['number'],
    modes: ['list', 'form'],
  });
  registerWidget('orderShippingAddress', {
    display: OrderShippingAddress,
    csvText: rawCsv,
    supportedTypes: ['json'],
    modes: ['list', 'form'],
  });
  registerWidget('orderProcessedAt', {
    display: OrderProcessedAt,
    csvText: rawCsv,
    supportedTypes: ['datetime'],
    modes: ['list', 'form'],
  });
  registerWidget('orderDeliveryDate', {
    display: OrderDeliveryDate,
    csvText: rawCsv,
    supportedTypes: ['date'],
    modes: ['list', 'form'],
  });
}
